import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { LotusShape, StarShape, SunShape } from "@/components/base/BrandShapes";
import SimulatorQuestion, { type Question } from "./components/SimulatorQuestion";
import SimulatorResult, { type RiskCategory, type PackageRecommendation } from "./components/SimulatorResult";
import { useTranslation } from "react-i18next";
import { isArabicLang } from "@/utils/localization";

// ─── Questions ────────────────────────────────────────────────────────────────
const getQuestions = (isAr: boolean): Question[] => [
  {
    id: "age",
    category: isAr ? "المعلومات الأساسية" : "Basic Information",
    categoryIcon: "ri-user-line",
    categoryColor: "teal",
    question: isAr ? "ما هي فئتك العمرية؟" : "What is your age group?",
    subtitle: isAr ? "العمر عامل محوري في تقييم المخاطر الصحية المستقبلية" : "Age is a pivotal factor in assessing future health risks",
    options: [
      { label: isAr ? "أقل من 30 سنة" : "Under 30 years", value: "under30", riskScore: 1, icon: "ri-seedling-line" },
      { label: isAr ? "30 – 40 سنة" : "30 – 40 years", value: "30-40", riskScore: 3, icon: "ri-plant-line" },
      { label: isAr ? "40 – 50 سنة" : "40 – 50 years", value: "40-50", riskScore: 5, icon: "ri-tree-line" },
      { label: isAr ? "50 – 60 سنة" : "50 – 60 years", value: "50-60", riskScore: 7, icon: "ri-ancient-gate-line" },
      { label: isAr ? "أكثر من 60 سنة" : "Over 60 years", value: "over60", riskScore: 9, icon: "ri-ancient-pavilion-line" },
    ],
  },
  {
    id: "family_history",
    category: isAr ? "التاريخ العائلي" : "Family History",
    categoryIcon: "ri-group-line",
    categoryColor: "violet",
    question: isAr ? "هل يوجد في عائلتك تاريخ لأي من هذه الأمراض؟" : "Is there a family history of any of these diseases?",
    subtitle: isAr ? "التاريخ العائلي من أقوى مؤشرات الاستعداد الجيني" : "Family history is one of the strongest indicators of genetic predisposition",
    multiSelect: true,
    options: [
      { label: isAr ? "أمراض القلب والشرايين" : "Cardiovascular diseases", value: "heart", riskScore: 8, icon: "ri-heart-pulse-line" },
      { label: isAr ? "السكري من النوع الثاني" : "Type 2 diabetes", value: "diabetes", riskScore: 7, icon: "ri-drop-line" },
      { label: isAr ? "السرطان (أي نوع)" : "Cancer (any type)", value: "cancer", riskScore: 9, icon: "ri-microscope-line" },
      { label: isAr ? "الزهايمر أو الخرف" : "Alzheimer's or dementia", value: "alzheimer", riskScore: 8, icon: "ri-brain-line" },
      { label: isAr ? "هشاشة العظام" : "Osteoporosis", value: "osteoporosis", riskScore: 6, icon: "ri-body-scan-line" },
      { label: isAr ? "لا يوجد تاريخ عائلي معروف" : "No known family history", value: "none", riskScore: 1, icon: "ri-checkbox-circle-line" },
    ],
  },
  {
    id: "lifestyle",
    category: isAr ? "نمط الحياة" : "Lifestyle",
    categoryIcon: "ri-run-line",
    categoryColor: "orange",
    question: isAr ? "كيف تصف نمط حياتك اليومي؟" : "How would you describe your daily lifestyle?",
    options: [
      { label: isAr ? "نشيط جداً — رياضة يومية" : "Very active — daily exercise", value: "very_active", riskScore: 1, icon: "ri-run-line" },
      { label: isAr ? "نشيط نسبياً — رياضة 3 مرات أسبوعياً" : "Relatively active — exercise 3 times a week", value: "active", riskScore: 3, icon: "ri-walk-line" },
      { label: isAr ? "خامل نسبياً — حركة محدودة" : "Relatively sedentary — limited movement", value: "sedentary", riskScore: 6, icon: "ri-sofa-line" },
      { label: isAr ? "خامل تماماً — جلوس معظم اليوم" : "Completely sedentary — sitting most of the day", value: "very_sedentary", riskScore: 9, icon: "ri-computer-line" },
    ],
  },
  {
    id: "diet",
    category: isAr ? "النظام الغذائي" : "Diet",
    categoryIcon: "ri-restaurant-line",
    categoryColor: "amber",
    question: isAr ? "كيف تصف نظامك الغذائي؟" : "How would you describe your diet?",
    options: [
      { label: isAr ? "صحي ومتوازن — خضروات وبروتين وحبوب كاملة" : "Healthy & balanced — vegetables, protein & whole grains", value: "healthy", riskScore: 1, icon: "ri-leaf-line" },
      { label: isAr ? "متوسط — أحياناً صحي وأحياناً لا" : "Moderate — sometimes healthy, sometimes not", value: "moderate", riskScore: 4, icon: "ri-scales-line" },
      { label: isAr ? "غير صحي — وجبات سريعة وسكريات عالية" : "Unhealthy — fast food & high sugars", value: "unhealthy", riskScore: 7, icon: "ri-restaurant-2-line" },
      { label: isAr ? "غير منتظم — لا نمط واضح" : "Irregular — no clear pattern", value: "irregular", riskScore: 6, icon: "ri-time-line" },
    ],
  },
  {
    id: "stress",
    category: isAr ? "الصحة النفسية" : "Mental Health",
    categoryIcon: "ri-mental-health-line",
    categoryColor: "rose",
    question: isAr ? "ما مستوى الضغط النفسي في حياتك؟" : "What is the level of stress in your life?",
    subtitle: isAr ? "الضغط المزمن يؤثر على الصحة الجسدية بشكل مباشر" : "Chronic stress directly affects physical health",
    options: [
      { label: isAr ? "منخفض — حياة هادئة ومتوازنة" : "Low — calm & balanced life", value: "low", riskScore: 1, icon: "ri-sun-line" },
      { label: isAr ? "متوسط — ضغط عمل معتاد" : "Moderate — normal work pressure", value: "moderate", riskScore: 4, icon: "ri-cloud-line" },
      { label: isAr ? "مرتفع — ضغط مستمر ومؤثر" : "High — constant & significant pressure", value: "high", riskScore: 7, icon: "ri-thunderstorms-line" },
      { label: isAr ? "مرتفع جداً — إرهاق مزمن وقلق دائم" : "Very high — chronic fatigue & constant anxiety", value: "very_high", riskScore: 9, icon: "ri-tornado-line" },
    ],
  },
  {
    id: "sleep",
    category: isAr ? "النوم والراحة" : "Sleep & Rest",
    categoryIcon: "ri-moon-line",
    categoryColor: "violet",
    question: isAr ? "كيف تصف جودة نومك؟" : "How would you describe your sleep quality?",
    options: [
      { label: isAr ? "ممتاز — 7-9 ساعات منتظمة" : "Excellent — regular 7-9 hours", value: "excellent", riskScore: 1, icon: "ri-moon-clear-line" },
      { label: isAr ? "جيد — 6-7 ساعات في الغالب" : "Good — mostly 6-7 hours", value: "good", riskScore: 3, icon: "ri-moon-line" },
      { label: isAr ? "متقطع — أستيقظ كثيراً" : "Interrupted — wake up frequently", value: "interrupted", riskScore: 6, icon: "ri-moon-foggy-line" },
      { label: isAr ? "سيء — أقل من 6 ساعات أو أرق مزمن" : "Poor — less than 6 hours or chronic insomnia", value: "poor", riskScore: 9, icon: "ri-moon-cloudy-line" },
    ],
  },
  {
    id: "symptoms",
    category: isAr ? "الأعراض الحالية" : "Current Symptoms",
    categoryIcon: "ri-stethoscope-line",
    categoryColor: "cyan",
    question: isAr ? "هل تعاني من أي من هذه الأعراض؟" : "Do you suffer from any of these symptoms?",
    subtitle: isAr ? "اختر كل ما ينطبق عليك" : "Select all that apply",
    multiSelect: true,
    options: [
      { label: isAr ? "تعب مزمن غير مفسَّر" : "Unexplained chronic fatigue", value: "fatigue", riskScore: 7, icon: "ri-battery-low-line" },
      { label: isAr ? "زيادة في الوزن صعبة الإنقاص" : "Weight gain difficult to lose", value: "weight", riskScore: 6, icon: "ri-scales-line" },
      { label: isAr ? "ضغط دم مرتفع أو حدودي" : "High or borderline blood pressure", value: "bp", riskScore: 8, icon: "ri-heart-pulse-line" },
      { label: isAr ? "سكر دم مرتفع أو مقاومة أنسولين" : "High blood sugar or insulin resistance", value: "glucose", riskScore: 8, icon: "ri-drop-line" },
      { label: isAr ? "آلام مفاصل أو عظام" : "Joint or bone pain", value: "joints", riskScore: 5, icon: "ri-body-scan-line" },
      { label: isAr ? "لا أعاني من أعراض واضحة" : "No obvious symptoms", value: "none", riskScore: 1, icon: "ri-checkbox-circle-line" },
    ],
  },
  {
    id: "checkups",
    category: isAr ? "الفحوصات الدورية" : "Routine Checkups",
    categoryIcon: "ri-file-chart-line",
    categoryColor: "teal",
    question: isAr ? "متى كان آخر فحص طبي شامل؟" : "When was your last comprehensive medical checkup?",
    options: [
      { label: isAr ? "خلال الـ 6 أشهر الماضية" : "Within the past 6 months", value: "recent", riskScore: 1, icon: "ri-calendar-check-line" },
      { label: isAr ? "خلال السنة الماضية" : "Within the past year", value: "year", riskScore: 3, icon: "ri-calendar-line" },
      { label: isAr ? "منذ أكثر من سنتين" : "More than 2 years ago", value: "two_years", riskScore: 6, icon: "ri-calendar-2-line" },
      { label: isAr ? "لم أجرِ فحصاً شاملاً من قبل" : "Never had a comprehensive checkup", value: "never", riskScore: 9, icon: "ri-close-circle-line" },
    ],
  },
  {
    id: "smoking",
    category: isAr ? "عوامل الخطر" : "Risk Factors",
    categoryIcon: "ri-lungs-line",
    categoryColor: "orange",
    question: isAr ? "ما علاقتك بالتدخين؟" : "What is your relation with smoking?",
    options: [
      { label: isAr ? "لم أدخن قط" : "Never smoked", value: "never", riskScore: 0, icon: "ri-checkbox-circle-line" },
      { label: isAr ? "مدخن سابق — توقفت" : "Former smoker — quit", value: "former", riskScore: 4, icon: "ri-history-line" },
      { label: isAr ? "مدخن حالي — أقل من علبة يومياً" : "Current smoker — less than a pack daily", value: "light", riskScore: 7, icon: "ri-alert-line" },
      { label: isAr ? "مدخن حالي — علبة أو أكثر يومياً" : "Current smoker — pack or more daily", value: "heavy", riskScore: 10, icon: "ri-error-warning-line" },
    ],
  },
  {
    id: "goal",
    category: isAr ? "هدفك من التقييم" : "Assessment Goal",
    categoryIcon: "ri-focus-3-line",
    categoryColor: "teal",
    question: isAr ? "ما الذي تبحث عنه بالتحديد؟" : "What specifically are you looking for?",
    subtitle: isAr ? "سيساعدنا هذا في توصية الباقة الأنسب لك" : "This will help us recommend the most suitable package",
    options: [
      { label: isAr ? "فهم مخاطري الصحية المستقبلية" : "Understanding my future health risks", value: "risk", riskScore: 0, icon: "ri-radar-line" },
      { label: isAr ? "تحسين طاقتي ومستوى حيويتي" : "Improving my energy & vitality", value: "energy", riskScore: 0, icon: "ri-flashlight-line" },
      { label: isAr ? "إدارة حالة صحية موجودة" : "Managing an existing health condition", value: "manage", riskScore: 0, icon: "ri-heart-pulse-line" },
      { label: isAr ? "الوقاية قبل ظهور الأعراض" : "Prevention before symptoms appear", value: "prevention", riskScore: 0, icon: "ri-shield-flash-line" },
    ],
  },
];

// ─── Risk calculation ─────────────────────────────────────────────────────────
function calculateRisks(answers: Record<string, string[]>, questionsList: Question[]): {
  overall: number;
  categories: RiskCategory[];
} {
  const getScore = (qId: string): number => {
    const q = questionsList.find((q) => q.id === qId);
    if (!q) return 0;
    const selected = answers[qId] ?? [];
    if (selected.length === 0) return 0;
    const scores = selected.map((v) => q.options.find((o) => o.value === v)?.riskScore ?? 0);
    // For multi-select, take average + bonus for multiple selections
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const bonus = scores.length > 1 ? Math.min(scores.length * 0.5, 2) : 0;
    return Math.min(avg + bonus, 10);
  };

  const ageScore = getScore("age");
  const familyScore = getScore("family_history");
  const lifestyleScore = getScore("lifestyle");
  const dietScore = getScore("diet");
  const stressScore = getScore("stress");
  const sleepScore = getScore("sleep");
  const symptomsScore = getScore("symptoms");
  const checkupsScore = getScore("checkups");
  const smokingScore = getScore("smoking");

  // Weighted categories
  const cardiac = Math.round(((familyScore * 0.35 + ageScore * 0.2 + smokingScore * 0.25 + lifestyleScore * 0.2) / 10) * 100);
  const metabolic = Math.round(((dietScore * 0.35 + symptomsScore * 0.3 + lifestyleScore * 0.2 + ageScore * 0.15) / 10) * 100);
  const neuro = Math.round(((familyScore * 0.3 + ageScore * 0.25 + stressScore * 0.25 + sleepScore * 0.2) / 10) * 100);
  const bone = Math.round(((familyScore * 0.3 + ageScore * 0.3 + lifestyleScore * 0.2 + dietScore * 0.2) / 10) * 100);
  const mental = Math.round(((stressScore * 0.45 + sleepScore * 0.3 + symptomsScore * 0.25) / 10) * 100);

  const overall = Math.round((cardiac * 0.25 + metabolic * 0.25 + neuro * 0.2 + bone * 0.15 + mental * 0.15));

  return {
    overall: Math.min(overall, 98),
    categories: [
      { id: "cardiac", label: "القلب والأوعية", icon: "ri-heart-pulse-line", score: Math.min(cardiac, 98) },
      { id: "metabolic", label: "الأيض والسكري", icon: "ri-scales-line", score: Math.min(metabolic, 98) },
      { id: "neuro", label: "الجهاز العصبي", icon: "ri-brain-line", score: Math.min(neuro, 98) },
      { id: "bone", label: "العظام والمفاصل", icon: "ri-body-scan-line", score: Math.min(bone, 98) },
      { id: "mental", label: "الصحة النفسية", icon: "ri-mental-health-line", score: Math.min(mental, 98) },
    ],
  };
}

// ─── Package recommendation logic ─────────────────────────────────────────────
function recommendPackages(
  overall: number,
  categories: RiskCategory[],
  answers: Record<string, string[]>,
  isAr: boolean
): { primary: PackageRecommendation; secondary?: PackageRecommendation } {
  const goal = answers["goal"]?.[0] ?? "risk";
  const hasFamily = (answers["family_history"] ?? []).some((v) => v !== "none");
  const neuroScore = categories.find((c) => c.id === "neuro")?.score ?? 0;
  const metabolicScore = categories.find((c) => c.id === "metabolic")?.score ?? 0;
  const mentalScore = categories.find((c) => c.id === "mental")?.score ?? 0;

  // High overall risk → Precision Medicine
  if (overall >= 65 || (hasFamily && overall >= 45)) {
    return {
      primary: {
        id: "precision-standard",
        name: isAr ? "منصة الطب الدقيق الشاملة" : "Comprehensive Precision Medicine Platform",
        category: isAr ? "الطب الدقيق" : "Precision Medicine",
        price: 2800,
        badge: isAr ? "الأكثر طلباً" : "Most Requested",
        icon: "ri-radar-line",
        accentColor: "violet",
        features: isAr
          ? ["تقييم عميق 120 دقيقة", "DNA Risk Score شامل (50+ مرض)", "تقييم العمر البيولوجي", "تحاليل أيضية ووظيفية", "خطة وقائية شخصية", "متابعة 6 أشهر"]
          : ["120-minute deep assessment", "Comprehensive DNA Risk Score (50+ diseases)", "Biological age assessment", "Metabolic & functional analyses", "Personalized preventive plan", "6-month follow-up"],
        reason: isAr
          ? "مستوى مخاطرك يستدعي تقييمًا جينيًا شاملًا مع خطة وقائية مبنية على بيولوجيتك الخاصة"
          : "Your risk level warrants a comprehensive genetic evaluation with a preventive plan built on your own biology",
      },
      secondary: {
        id: "dna-complete",
        name: isAr ? "باقة DNA الشاملة" : "Comprehensive DNA Package",
        category: isAr ? "الجينات والDNA" : "Genetics & DNA",
        price: 4200,
        badge: isAr ? "حصري" : "Exclusive",
        icon: "ri-focus-3-line",
        accentColor: "violet",
        features: isAr
          ? ["DNA Risk Score شامل (50+ مرض)", "DNA النفسي", "تقييم العمر البيولوجي", "جلسة تفسير شاملة 90 دقيقة", "خطة وقائية متكاملة", "متابعة 6 أشهر"]
          : ["Comprehensive DNA Risk Score (50+ diseases)", "Psychological DNA", "Biological age assessment", "90-minute comprehensive interpretation session", "Integrated preventive plan", "6-month follow-up"],
        reason: isAr
          ? "للحصول على أعمق صورة جينية ممكنة مع تحليل نفسي وبيولوجي متكامل"
          : "To get the deepest possible genetic picture with integrated psychological and biological analysis",
      },
    };
  }

  // High neuro risk → Alzheimer prevention
  if (neuroScore >= 60) {
    return {
      primary: {
        id: "neuro-standard",
        name: isAr ? "باقة الوقاية من الزهايمر" : "Alzheimer's Prevention Package",
        category: isAr ? "الوقاية العصبية" : "Neuro-prevention",
        price: 3800,
        badge: isAr ? "الأكثر طلباً" : "Most Requested",
        icon: "ri-shield-flash-line",
        accentColor: "violet",
        features: isAr
          ? ["تقييم عميق 90 دقيقة", "التقييم الجيني للزهايمر (APOE)", "MRI دماغ متقدم", "تحاليل المؤشرات الحيوية العصبية", "خطة وقائية شخصية", "متابعة سنة"]
          : ["90-minute deep assessment", "APOE genetic assessment for Alzheimer's", "Advanced brain MRI", "Neuro-biomarker analyses", "Personalized preventive plan", "1-year follow-up"],
        reason: isAr
          ? "مؤشرات المخاطر العصبية لديك تستدعي تقييمًا مبكرًا للوقاية من التنكس العصبي"
          : "Your neurological risk indicators warrant early assessment to prevent neurodegeneration",
      },
      secondary: {
        id: "dna-basic",
        name: isAr ? "DNA Risk Score الأساسي" : "Basic DNA Risk Score",
        category: isAr ? "الجينات والDNA" : "Genetics & DNA",
        price: 1800,
        icon: "ri-dna-line",
        accentColor: "violet",
        features: isAr
          ? ["تحليل DNA (20 مرض رئيسي)", "تقرير المخاطر الجينية", "جلسة تفسير النتائج", "توصيات وقائية"]
          : ["DNA analysis (20 major diseases)", "Genetic risk report", "Results interpretation session", "Preventive recommendations"],
        reason: isAr
          ? "خطوة أولى لفهم استعداداتك الجينية بتكلفة معقولة"
          : "A first step to understanding your genetic predispositions at an affordable cost",
      },
    };
  }

  // High metabolic risk
  if (metabolicScore >= 60) {
    return {
      primary: {
        id: "metabolic-standard",
        name: isAr ? "باقة السمنة والخلل الأيضي" : "Obesity & Metabolic Dysfunction Package",
        category: isAr ? "الخلل الأيضي" : "Metabolic Dysfunction",
        price: 2200,
        badge: isAr ? "الأكثر طلباً" : "Most Requested",
        icon: "ri-flask-line",
        accentColor: "orange",
        features: isAr
          ? ["تقييم أيضي عميق 120 دقيقة", "تحاليل MASLD المتخصصة", "Fibroscan الكبد", "التقييم الجيني الأيضي", "خطة علاجية شخصية", "متابعة 3 أشهر"]
          : ["120-minute deep metabolic assessment", "Specialized MASLD analyses", "Liver Fibroscan", "Genetic metabolic assessment", "Personalized treatment plan", "3-month follow-up"],
        reason: isAr
          ? "مؤشراتك الأيضية تستدعي تقييمًا عميقًا للخلل الأيضي وخطة علاجية مخصصة"
          : "Your metabolic indicators warrant a deep assessment of metabolic dysfunction and a tailored treatment plan",
      },
      secondary: {
        id: "iv-energy",
        name: isAr ? "باقة الطاقة الخلوية" : "Cellular Energy Package",
        category: isAr ? "العلاجات الوريدية" : "IV Therapies",
        price: 3200,
        badge: isAr ? "الأكثر طلباً" : "Most Requested",
        icon: "ri-flashlight-line",
        accentColor: "cyan",
        features: isAr
          ? ["تحاليل وظيفية قبل البدء", "5 جلسات بروتوكول الطاقة الخلوية", "دعم الميتوكوندريا", "مضادات الأكسدة العالية", "متابعة شهر"]
          : ["Functional analyses before starting", "5 cellular energy protocol sessions", "Mitochondrial support", "High antioxidants", "1-month follow-up"],
        reason: isAr
          ? "لتحسين مستوى الطاقة الخلوية وتسريع التعافي الأيضي"
          : "To improve cellular energy levels and accelerate metabolic recovery",
      },
    };
  }

  // High mental stress
  if (mentalScore >= 60 || goal === "energy") {
    return {
      primary: {
        id: "psych-standard",
        name: isAr ? "باقة الرعاية النفسية" : "Psychiatric Care Package",
        category: isAr ? "الطب النفسي" : "Psychiatry",
        price: 2500,
        badge: isAr ? "الأكثر طلباً" : "Most Requested",
        icon: "ri-brain-line",
        accentColor: "rose",
        features: isAr
          ? ["تقييم نفسي عميق 90 دقيقة", "DNA النفسي", "تقييم الوظائف المعرفية", "4 جلسات متابعة", "خطة علاجية شخصية"]
          : ["90-minute deep psychological assessment", "Psychological DNA", "Cognitive function assessment", "4 follow-up sessions", "Personalized treatment plan"],
        reason: isAr
          ? "مستوى الضغط النفسي لديك يستدعي تقييمًا عميقًا يربط البيولوجي بالنفسي"
          : "Your level of psychological stress warrants a deep assessment connecting the biological and psychological aspects",
      },
      secondary: {
        id: "iv-energy",
        name: isAr ? "باقة الطاقة الخلوية" : "Cellular Energy Package",
        category: isAr ? "العلاجات الوريدية" : "IV Therapies",
        price: 3200,
        icon: "ri-flashlight-line",
        accentColor: "cyan",
        features: isAr
          ? ["تحاليل وظيفية قبل البدء", "5 جلسات بروتوكول الطاقة الخلوية", "دعم الميتوكوندريا", "مضادات الأكسدة العالية", "متابعة شهر"]
          : ["Functional analyses before starting", "5 cellular energy protocol sessions", "Mitochondrial support", "High antioxidants", "1-month follow-up"],
        reason: isAr
          ? "لاستعادة الطاقة الخلوية وتحسين مستوى الحيوية"
          : "To restore cellular energy and improve vitality levels",
      },
    };
  }

  // Low-moderate risk → Deep assessment
  return {
    primary: {
      id: "deep-standard",
      name: isAr ? "التقييم العميق الشامل" : "Comprehensive Deep Assessment",
      category: isAr ? "التقييم العميق" : "Deep Assessment",
      price: 1400,
      badge: isAr ? "الأكثر طلباً" : "Most Requested",
      icon: "ri-stack-line",
      accentColor: "teal",
      features: isAr
        ? ["جلسة تقييم 120 دقيقة", "تحاليل وظيفية وأيضية متقدمة", "قراءة المخاطر الحالية والمستقبلية", "استشارة استشاري أول", "تقرير سريري + خطة شخصية", "متابعة شهر"]
        : ["120-minute assessment session", "Advanced functional & metabolic analyses", "Current & future risk assessment", "Senior consultant consultation", "Clinical report + personalized plan", "1-month follow-up"],
      reason: isAr
        ? "نقطة البداية المثالية — تقييم عميق يبني صورة واضحة عن مسارك الصحي"
        : "The perfect starting point — a deep assessment building a clear picture of your health journey",
    },
    secondary: {
      id: "dna-basic",
      name: isAr ? "DNA Risk Score الأساسي" : "Basic DNA Risk Score",
      category: isAr ? "الجينات والDNA" : "Genetics & DNA",
      price: 1800,
      icon: "ri-dna-line",
      accentColor: "violet",
      features: isAr
        ? ["تحليل DNA (20 مرض رئيسي)", "تقرير المخاطر الجينية", "جلسة تفسير النتائج", "توصيات وقائية"]
        : ["DNA analysis (20 major diseases)", "Genetic risk report", "Results interpretation session", "Preventive recommendations"],
      reason: isAr
        ? "لفهم استعداداتك الجينية وبناء خطة وقائية مبكرة"
        : "To understand your genetic predispositions and build an early preventive plan",
    },
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DNASimulatorPage() {
  const { t, i18n } = useTranslation();
  const isAr = isArabicLang(i18n.language);
  const questions = useMemo(() => getQuestions(isAr), [isAr]);

  const [currentStep, setCurrentStep] = useState<"intro" | "quiz" | "result">("intro");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const progressRef = useRef<HTMLDivElement>(null);

  const progress = Math.round((currentQuestion / questions.length) * 100);

  const currentQ = questions[currentQuestion];
  const currentAnswer = answers[currentQ?.id] ?? [];

  const canProceed = currentAnswer.length > 0;

  const handleAnswer = (values: string[]) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: values }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((q) => q + 1);
    } else {
      setCurrentStep("result");
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((q) => q - 1);
    } else {
      setCurrentStep("intro");
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setCurrentStep("intro");
  };

  const results = useMemo(() => {
    if (currentStep !== "result") return null;
    return calculateRisks(answers, questions);
  }, [currentStep, answers, questions]);

  const recommendation = useMemo(() => {
    if (!results) return null;
    return recommendPackages(results.overall, results.categories, answers, isAr);
  }, [results, answers, isAr]);

  useEffect(() => {
    if (currentStep === "quiz") {
      progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentStep]);

  return (
    <div className="min-h-screen bg-brand-cream-50 font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-gradient-to-br from-brand-forest-900 via-brand-forest-800 to-brand-forest-700">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=DNA%20double%20helix%20genetic%20code%20abstract%20visualization%20dark%20forest%20green%20background%20glowing%20strands%20precision%20medicine%20genomics%20sophisticated%20scientific&width=1440&height=500&seq=dna-sim-hero-v1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-forest-900/40 via-transparent to-brand-forest-900/60" />

        {/* Decorative shapes */}
        <div className="absolute top-8 right-12 opacity-[0.12] pointer-events-none">
          <LotusShape size={100} color="#e8dcc8" />
        </div>
        <div className="absolute bottom-6 left-16 opacity-[0.10] pointer-events-none">
          <StarShape size={72} color="#e8dcc8" />
        </div>
        <div className="absolute top-1/2 -translate-y-1/2 left-1/4 opacity-[0.06] pointer-events-none">
          <SunShape size={56} color="#e8dcc8" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 backdrop-blur-sm">
            <i className="ri-dna-line text-brand-cream-300"></i>
            {t("dna.intro.badge")}
          </span>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
            DNA Risk Score
            <span className="block text-brand-cream-300 text-2xl md:text-3xl mt-1 font-bold">
              {t("dna.intro.title")}
            </span>
          </h1>
          <p className="text-brand-cream-200 text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            {t("dna.intro.desc")}
          </p>

          {currentStep === "intro" && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setCurrentStep("quiz")}
                className="bg-brand-cream-300 text-brand-forest-900 font-bold px-8 py-3.5 rounded-full hover:bg-brand-cream-200 transition-colors cursor-pointer text-sm whitespace-nowrap"
              >
                <i className="ri-play-circle-line ml-2 text-base"></i>
                {t("dna.intro.start")}
              </button>
              <Link
                to="/services/dna-risk"
                className="border-2 border-white/40 text-white font-bold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer text-sm whitespace-nowrap"
              >
                <i className="ri-information-line ml-2"></i>
                {t("dna.intro.learn_more")}
              </Link>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-10">
            {[
              { value: "10", label: t("dna.stats.questions") },
              { value: "5", label: t("dna.stats.categories") },
              { value: "100%", label: t("dna.stats.free") },
            ].map((s) => (
              <div key={s.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <div className="text-xl font-black text-white">{s.value}</div>
                <div className="text-brand-cream-300 text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Cards */}
      {currentStep === "intro" && (
        <section className="max-w-5xl mx-auto px-4 md:px-8 py-14">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-gray-900 mb-3">{t("dna.intro.how_works")}</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              {t("dna.intro.how_works_desc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                step: "01",
                icon: "ri-questionnaire-line",
                title: t("dna.intro.step1_title"),
                desc: t("dna.intro.step1_desc"),
                color: "bg-brand-forest-50 text-brand-forest-700",
              },
              {
                step: "02",
                icon: "ri-bar-chart-2-line",
                title: t("dna.intro.step2_title"),
                desc: t("dna.intro.step2_desc"),
                color: "bg-violet-50 text-violet-700",
              },
              {
                step: "03",
                icon: "ri-award-line",
                title: t("dna.intro.step3_title"),
                desc: t("dna.intro.step3_desc"),
                color: "bg-amber-50 text-amber-700",
              },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-2xl border border-brand-cream-200 p-6 text-center">
                <div className="text-4xl font-black text-brand-cream-300 mb-3">{item.step}</div>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4 ${item.color.split(" ")[0]}`}>
                  <i className={`${item.icon} text-xl ${item.color.split(" ")[1]}`}></i>
                </div>
                <h3 className="font-black text-gray-900 text-base mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Risk categories preview */}
          <div className="bg-white rounded-2xl border border-brand-cream-200 p-6 mb-10">
            <h3 className="font-black text-gray-900 text-base mb-5 text-center">{t("dna.intro.eval_title")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { id: "cardiac", icon: "ri-heart-pulse-line", color: "bg-rose-50 text-rose-600" },
                { id: "metabolic", icon: "ri-scales-line", color: "bg-orange-50 text-orange-600" },
                { id: "neuro", icon: "ri-brain-line", color: "bg-violet-50 text-violet-600" },
                { id: "bone", icon: "ri-body-scan-line", color: "bg-amber-50 text-amber-600" },
                { id: "mental", icon: "ri-mental-health-line", color: "bg-brand-forest-50 text-brand-forest-600" },
              ].map((cat) => (
                <div key={cat.id} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-brand-cream-50">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${cat.color.split(" ")[0]}`}>
                    <i className={`${cat.icon} text-lg ${cat.color.split(" ")[1]}`}></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center">{t("dna.result." + cat.id)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setCurrentStep("quiz")}
              className="bg-brand-forest-700 hover:bg-brand-forest-800 text-white font-bold px-10 py-4 rounded-full transition-colors cursor-pointer text-base whitespace-nowrap"
            >
              <i className="ri-dna-line ml-2 text-lg"></i>
              {t("dna.intro.start_btn")}
            </button>
            <p className="text-xs text-gray-400 mt-3">{t("dna.intro.free_note")}</p>
          </div>
        </section>
      )}

      {/* Quiz */}
      {currentStep === "quiz" && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          {/* Progress */}
          <div ref={progressRef} className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-brand-forest-700">
                {t("dna.q.question_label")} {currentQuestion + 1} {t("dna.q.of_label")} {questions.length}
              </span>
              <span className="text-xs text-gray-400">{progress}% {t("dna.quiz.progress_completed")}</span>
            </div>
            <div className="w-full bg-brand-cream-200 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-brand-forest-600 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            {/* Step dots */}
            <div className="flex gap-1 mt-3 justify-center flex-wrap">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i < currentQuestion
                      ? "bg-brand-forest-600 w-4"
                      : i === currentQuestion
                      ? "bg-brand-forest-400 w-6"
                      : "bg-brand-cream-300 w-1.5"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl border border-brand-cream-200 p-6 md:p-8 mb-6">
            <SimulatorQuestion
              question={currentQ}
              selected={currentAnswer}
              onSelect={handleAnswer}
              questionIndex={currentQuestion}
              totalQuestions={questions.length}
            />
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors cursor-pointer px-4 py-2.5 rounded-xl border border-brand-cream-200 hover:bg-brand-cream-50 whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i>
              {t("dna.quiz.prev")}
            </button>

            <div className="text-xs text-gray-400 text-center hidden sm:block">
              {currentQ.multiSelect ? t("dna.q.multiselect_label") : t("dna.q.singleselect_label")}
            </div>

            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                canProceed
                  ? "bg-brand-forest-700 hover:bg-brand-forest-800 text-white"
                  : "bg-brand-cream-200 text-brand-cream-400 cursor-not-allowed"
              }`}
            >
              {currentQuestion === questions.length - 1 ? (
                <>
                  <i className="ri-bar-chart-2-line"></i>
                  {t("dna.quiz.show_results")}
                </>
              ) : (
                <>
                  {t("dna.quiz.next")}
                  <i className="ri-arrow-left-line"></i>
                </>
              )}
            </button>
          </div>
        </section>
      )}

      {/* Result */}
      {currentStep === "result" && results && recommendation && (
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-brand-forest-100 text-brand-forest-700 text-xs font-bold px-4 py-1.5 rounded-full mb-4">
              <i className="ri-check-double-line"></i>
              {t("dna.result.analyzed")}
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">{t("dna.result.hero_title")}</h2>
            <p className="text-gray-500 text-sm">{t("dna.result.hero_desc")}</p>
          </div>

          <SimulatorResult
            overallScore={results.overall}
            riskCategories={results.categories}
            primaryPackage={recommendation.primary}
            secondaryPackage={recommendation.secondary}
            onRetake={handleRetake}
            answers={answers}
          />
        </section>
      )}

      {/* Bottom CTA — only on intro */}
      {currentStep === "intro" && (
        <section className="py-12 bg-brand-forest-900 relative overflow-hidden">
          <div className="absolute top-4 right-10 opacity-[0.10] pointer-events-none">
            <StarShape size={80} color="#e8dcc8" />
          </div>
          <div className="absolute bottom-4 left-10 opacity-[0.08] pointer-events-none">
            <LotusShape size={70} color="#e8dcc8" />
          </div>
          <div className="relative max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl font-black text-white mb-3">{t("dna.bottom.title")}</h2>
            <p className="text-brand-cream-200 text-sm mb-7 leading-relaxed">
              {t("dna.bottom.desc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/booking"
                className="bg-brand-cream-300 text-brand-forest-900 font-bold px-7 py-3 rounded-full hover:bg-brand-cream-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-calendar-check-line ml-2"></i>
                {t("dna.bottom.book")}
              </Link>
              <Link
                to="/services/dna-risk"
                className="border-2 border-white/40 text-white font-bold px-7 py-3 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-information-line ml-2"></i>
                {t("dna.bottom.learn")}
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
